import { School, schoolDisplayName } from '../models'

export const formatSchoolLocation = (school: School): string => {
    const { city, state } = school.address
    const parts = [city, state].filter((p) => p && String(p).trim())
    return parts.join(', ')
}

/** Words skipped when building initials (keep university/state/college — needed for LSU, UM, etc.). */
const ACRONYM_STOP_WORDS = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'by',
    'for',
    'in',
    'of',
    'or',
    'the',
    'to',
])

const tokenizeTitleWords = (title: string): string[] =>
    title
        .split(/[^a-zA-Z]+/)
        .map((w) => w.toLowerCase())
        .filter(Boolean)

/** Uppercase initials from significant words, e.g. "Louisiana State University" → "LSU". */
const acronymFromTitle = (title: string): string => {
    const tokens = tokenizeTitleWords(title)
    if (tokens.length === 0) return ''
    const significant = tokens.filter((t) => !ACRONYM_STOP_WORDS.has(t))
    const use = significant.length > 0 ? significant : tokens
    return use.map((w) => w[0].toUpperCase()).join('')
}

const titleMatchesAcronym = (
    title: string | null | undefined,
    acronymUpper: string,
) => {
    if (typeof title !== 'string' || !title.trim() || !acronymUpper)
        return false
    return acronymFromTitle(title) === acronymUpper
}

/** 2–5 letters only: treat as possible acronym (fewer than 6 characters). Case-insensitive input normalized to uppercase for match. */
export const isShortLetterOnlyAcronymQuery = (raw: string): boolean =>
    /^[a-zA-Z]{2,5}$/.test(raw.trim())

const schoolTitleHaystacks = (
    option: School,
): (string | null | undefined)[] => [
    schoolDisplayName(option),
    option.name,
    option.effective_display_name,
    option.google_places_display_name,
]

const schoolAcronymMatches = (option: School, acronymUpper: string): boolean =>
    schoolTitleHaystacks(option).some(
        (t) => typeof t === 'string' && titleMatchesAcronym(t, acronymUpper),
    )

const schoolSubstringMatches = (option: School, qLower: string): boolean => {
    const haystacks = [
        ...schoolTitleHaystacks(option),
        formatSchoolLocation(option),
    ]
    return haystacks.some(
        (s) => typeof s === 'string' && s.toLowerCase().includes(qLower),
    )
}

export const schoolMatchesSearchQuery = (
    option: School,
    inputValue: string,
) => {
    const raw = inputValue.trim()
    if (!raw) return true
    const qLower = raw.toLowerCase()

    const substringHit = schoolSubstringMatches(option, qLower)

    let acronymHit = false
    if (isShortLetterOnlyAcronymQuery(raw)) {
        acronymHit = schoolAcronymMatches(option, raw.toUpperCase())
    }

    return substringHit || acronymHit
}

/** When the query looks like a short acronym, surface acronym matches ahead of substring-only hits. */
const compareSchoolOptionsForShortAcronymQuery = (
    a: School,
    b: School,
    acronymUpper: string,
): number => {
    const ma = schoolAcronymMatches(a, acronymUpper) ? 0 : 1
    const mb = schoolAcronymMatches(b, acronymUpper) ? 0 : 1
    return ma - mb
}

export const schoolOptionChipLabel = (option: School) => {
    const title = schoolDisplayName(option)
    const location = formatSchoolLocation(option)
    return location ? `${title} — ${location}` : title
}

export const filterSchoolAutocompleteOptions = (
    options: School[],
    inputValue: string,
    limit = 10,
): School[] => {
    const raw = inputValue.trim()
    if (!raw) return options.slice(0, limit)

    const filtered = options.filter((option) =>
        schoolMatchesSearchQuery(option, inputValue),
    )

    if (isShortLetterOnlyAcronymQuery(raw)) {
        const acronymUpper = raw.toUpperCase()
        filtered.sort((a, b) =>
            compareSchoolOptionsForShortAcronymQuery(a, b, acronymUpper),
        )
    }

    return filtered.slice(0, limit)
}
